













































































































import { computed, defineComponent, reactive, ref, toRefs, watch } from '@vue/composition-api';
import Sidebar from '@/components/Sidebar.vue';
import InputGroup from '@/components/InputGroup.vue';
import Button from '@/components/Button.vue';
import useSaveButton from '@/composables/buttons/useSaveButton';
import useCancelButton from '@/composables/buttons/useCancelButton';
import {
  TemplatesDeviceResponse,
  useGetAuthoritiesQuery,
  useGetDeviceByDeviceIdQuery,
  useGetDevicesQuery,
  useTemplatesForDeviceQuery
} from '@/graphql/types';
import { useResult } from '@vue/apollo-composable';
import useVuelidate from '@vuelidate/core';
import Select2 from '@/components/Select2.vue';
import IMenu from '@/interfaces/IMenu';
import Dropdown from '@/components/Dropdown.vue';
import useDefaultStatusS2Selections from '@/composables/useDefaultStatusS2Selections';
import { S2Selection } from '@/types/s2selection';
import { FilterState } from '@/types/enums';
import useCreateDeviceMutation from '@/views/Devices/composables/useCreateDeviceMutation';
import useUpdateDeviceMutation from '@/views/Devices/composables/useUpdateDeviceMutation';
import useDeleteDeviceMutation from '@/views/Devices/composables/useDeleteDeviceMutation';
import { required, withI18nMessage, deviceIdRequired, templateRequired } from '@/utilities/validators';
import Table from '@/components/Table.vue';
import { TableField } from '@/types/TableField';
import useProfile from '@/composables/useProfile';
import useNavigateToDevices from '@/composables/useNavigateToDevices';
export default defineComponent({
  props: {
    deviceId: {
      type: String,
      required: false
    },
    organisationId: {
      type: String,
      required: false
    }
  },
  components: { Sidebar, InputGroup, Button, Select2, Dropdown, Table },
  setup(props, { root }) {
    const user = useProfile();
    const isNew = ref(props.deviceId == null);
    const device = reactive({
      id: '',
      name: undefined,
      stationNumber: undefined,
      deviceId: '',
      templateId: '',
      organisationId: props.organisationId ?? '',
      isActive: true
    });
    const organisationId = ref(props.organisationId);
    const status = ref(null as S2Selection<FilterState> | null);
    const statusList = useDefaultStatusS2Selections().filter(x => x.object !== FilterState.All);
    const template = ref(null as S2Selection<TemplatesDeviceResponse> | null);
    const templateSelections: S2Selection<TemplatesDeviceResponse>[] = [];
    const hasChanges = ref<boolean | null>(null);
    const latestPayload = ref<string | undefined>('');
    const perPage = 10;
    if (isNew.value) hasChanges.value = false;

    const { result, loading, refetch } = useGetDeviceByDeviceIdQuery(
      () => ({ deviceId: props.deviceId! }),
      () => ({
        enabled: !isNew.value,
        fetchPolicy: 'network-only'
      })
    );

    const authoritiesQuery = useGetAuthoritiesQuery({
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
      enabled: !isNew.value
    });

    const templatesQuery = useTemplatesForDeviceQuery({
      fetchPolicy: 'cache-and-network'
    });

    const devicesQuery = useGetDevicesQuery(
      () => ({
        id: organisationId
      }),
      () => ({
        fetchPolicy: 'cache-and-network',
        enabled: !!organisationId.value
      })
    );

    const hasDevice = () => {
      return devicesQuery.result.value?.devices.some(
        (element: any) =>
          element?.deviceId?.toLowerCase() === device.deviceId?.toLowerCase() && element?.id != device.id
      );
    };

    const uniqueDeviceIdValidator = () => {
      if (device.deviceId == '') return true;

      if (hasDevice()) return false;
      return true;
    };

    const v$ = useVuelidate(
      {
        ...useValidations(uniqueDeviceIdValidator)
      },
      { ...toRefs(device) },
      { $autoDirty: true }
    );

    watch(
      () => useResult(result).value,
      newValue => {
        if (newValue) {
          Object.assign(device, newValue);
          organisationId.value = device.organisationId;
          handleRedirect();
        }
        const selectedStatus = statusList.find(
          x => x.object === (newValue?.isActive ? FilterState.Active : FilterState.Inactive)
        );
        if (selectedStatus) {
          status.value = selectedStatus;
        }

        setSelectedTemplate(newValue?.templateId);

        if (newValue?.telemetryData?.latestPayload !== '') {
          latestPayload.value = newValue?.telemetryData?.latestPayload;
        }

        v$.value.$reset();
      },
      { immediate: true }
    );

    watch(
      () => useResult(templatesQuery.result).value,
      newValue => {
        if (newValue) {
          newValue.forEach(element => {
            templateSelections?.push(
              new S2Selection<TemplatesDeviceResponse>(element.id, element.name, element, templateResult(element))
            );
          });
          setSelectedTemplate(device.templateId);
          templateSelections.sort((a, b) => a.text.localeCompare(b.text, 'da'));
        }
      },
      { immediate: true }
    );

    watch(
      () => [device?.id, device.stationNumber, device.deviceId, device.isActive, device.templateId],
      () => {
        if (loading.value) return;
        if (hasChanges.value == null) {
          hasChanges.value = false;
        } else {
          hasChanges.value = true;
        }
      },
      { deep: true }
    );

    watch(
      () => status,
      newValue => {
        device.isActive = newValue.value?.object === FilterState.Active;
      },
      { deep: true }
    );

    watch(
      () => template,
      newValue => {
        device.templateId = newValue.value?.object?.id;
      },
      { deep: true }
    );

    const createMutation = useCreateDeviceMutation(device);
    createMutation.onDone(response => {
      root.$emit('refetchDevices', response.data?.createDevice);
      root.$router.back();
    });

    const updateMutation = useUpdateDeviceMutation(device);
    updateMutation.onDone(() => {
      root.$emit('refetchDevices');
      root.$router.back();
    });

    const deleteMutation = useDeleteDeviceMutation(device);
    deleteMutation.onDone(() => {
      root.$emit('refetchDevices');
      root.$router.back();
    });

    // customized select2 option template
    const templateResult = (option: any) => {
      let html = `<div id="${option?.object?.id}">`;
      html += '<div>' + option?.object?.name + '</div>';
      if (option?.object?.description) {
        html += '<small>' + option?.object?.description + '</small>';
        html += '<br/>';
      }
      html += '<small>' + option?.object?.attributes.length + ' attributter</small>';
      html += '</div>';
      return html;
    };

    function setSelectedTemplate(templateId: string) {
      if (templateId !== '') {
        const selectedTemplate = templateSelections.find(x => x.object?.id === templateId);
        if (selectedTemplate) {
          template.value = selectedTemplate;
        }
      }
    }

    function handleRedirect() {
      if (device.deviceId === '') {
        // Redirect if system gets weried unrecognized device ID just sent user to list view (device not found)
        useNavigateToDevices(root.$router);
      } else {
        // User may have both admin and municipality roles
        // Do the check to make sure if the logged user has only the municipality role
        if (!user.isAdmin.value && user.isAuthority.value) {
          watch(
            () => useResult(authoritiesQuery.result).value,
            newValue => {
              if (newValue) {
                const currentAuthority = newValue.find(a => a.cvr == user.profile.value.cvr);

                // Redirect if the device does not belong to the logged in user's organisation
                // Redirect if user logs in as municipality (isAuthority) role and the device is deleted
                if (currentAuthority?.id !== device.organisationId) {
                  useNavigateToDevices(root.$router);
                }
              }
            },
            { immediate: true }
          );
        }
      }
    }

    return {
      isNew,
      v$,
      device,
      statusList,
      status,
      template,
      templateSelections,
      templateResult,
      isReadOnly: user.isReadOnly,
      perPage,
      templatesLoading: computed(() => templatesQuery.loading.value),
      loading: computed(
        () =>
          loading.value ||
          createMutation.loading.value ||
          updateMutation.loading.value ||
          deleteMutation.loading.value
      ),
      primaryButton: isNew.value
        ? {
            ...useSaveButton(async () => {
              if (await v$.value.$validate()) createMutation.call();
            })
          }
        : {
            ...useSaveButton(async () => {
              if (await v$.value.$validate()) updateMutation.call();
            }),
            i18n: 'common.update'
          },
      secondaryButton: useCancelButton(() => root.$router.back()),
      menuItems: computed<IMenu[]>(() => [
        {
          name: root.$tc('common.delete'),
          icon: 'trash-alt',
          requireConfirmation: true,
          confirmationTitle: 'Slet enheden',
          confirmationMessage: 'Vil du slette denne enhed?',
          primaryButton: {
            i18n: 'common.delete',
            variant: 'danger'
          },
          disabled: () => isNew.value,
          command: () => deleteMutation.call()
        }
      ]),
      attributeTemplateTableFields: computed<TableField[]>(() => [
        {
          key: 'sc1187Name',
          label: root.$tc('templates.attribute.name.label'),
          sortable: true,
          filter: false,
          width: '30%'
        },
        {
          key: 'sc1009Name',
          label: root.$tc('templates.attribute.unit.label'),
          sortable: true,
          filter: false,
          width: '15%'
        },
        {
          key: 'sensorMapping',
          label: root.$tc('templates.attribute.sensor_mapping.label'),
          sortable: true,
          filter: false,
          width: '15%'
        },
        {
          key: 'description',
          label: root.$tc('templates.attribute.description.label'),
          sortable: true,
          filter: false,
          width: '40%'
        }
      ]),
      latestPayload: computed(() => latestPayload.value)
    };
  }
});

function useValidations(uniqueDeviceIdValidator: () => boolean) {
  return {
    deviceId: {
      deviceIdRequired,
      uniqueDeviceId: withI18nMessage(uniqueDeviceIdValidator)
    },
    stationNumber: {},
    templateId: {
      templateRequired
    },
    name: {},
    isActive: {
      required
    }
  };
}
